import React from 'react';
import styled, { keyframes } from 'styled-components';
import DSBrainAI from '../components/logos/DSBrainAI';
import CTCoin from '../components/logos/CTCoin';

// ── Styles ──────────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: #EEF4FF;
  padding: 48px 56px;
  font-family: system-ui, -apple-system, sans-serif;
`;
const H1 = styled.h1`font-size: 22px; font-weight: 700; color: #1A2744; margin: 0 0 4px;`;
const Subtitle = styled.p`font-size: 14px; color: #64748B; margin: 0 0 48px;`;
const Section = styled.section`margin-bottom: 56px;`;
const SectionTitle = styled.h2`
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.1em; color: #94A3B8; margin: 0 0 20px;
`;
const LogoGrid = styled.div`display: flex; gap: 24px; flex-wrap: wrap;`;
const LogoCard = styled.div`
  background: #fff; border: 1px solid #E2EAF4; border-radius: 16px;
  padding: 28px 28px 20px; display: flex; flex-direction: column;
  align-items: center; gap: 20px; min-width: 220px;
`;
const FeaturedCard = styled(LogoCard)`
  flex-direction: row; align-items: flex-start; gap: 40px;
  min-width: unset; width: 100%; padding: 36px 40px;
`;
const SwatchRow = styled.div`display: flex; align-items: flex-end; gap: 20px;`;
const SwatchLabel = styled.div`font-size: 10px; color: #94A3B8; text-align: center; margin-top: 6px;`;
const LogoName = styled.div`font-size: 13px; font-weight: 600; color: #1A2744;`;
const LogoDesc = styled.div`font-size: 11px; color: #94A3B8; text-align: center; max-width: 160px;`;
const FeaturedInfo = styled.div`display: flex; flex-direction: column; gap: 12px; justify-content: center;`;
const FeaturedTitle = styled.div`font-size: 18px; font-weight: 700; color: #1A2744;`;
const FeaturedDesc = styled.div`font-size: 13px; color: #64748B; line-height: 1.6; max-width: 320px;`;
const Tag = styled.span`
  display: inline-block; padding: 3px 10px; border-radius: 20px;
  background: #EEF4FF; border: 1px solid #E2EAF4;
  font-size: 11px; font-weight: 600; color: #0EA5E9; margin-right: 6px;
`;
const WordmarkRow = styled.div`
  display: flex; align-items: center; gap: 12px;
  background: #EEF4FF; border-radius: 12px; padding: 12px 20px;
  margin-top: 8px;
`;
const WordmarkText = styled.div`font-size: 16px; font-weight: 700; color: #1A2744; letter-spacing: -0.01em;`;
const WordmarkSub = styled.div`font-size: 11px; color: #94A3B8; letter-spacing: 0.02em;`;

// ── Clinical Token spinning coin ────────────────────────────────────────────
const rockCoin = keyframes`
  0%   { transform: perspective(700px) rotateY(-22deg) rotateX(4deg); }
  50%  { transform: perspective(700px) rotateY(22deg)  rotateX(-4deg); }
  100% { transform: perspective(700px) rotateY(-22deg) rotateX(4deg); }
`;

const SpinCoin = styled.div`
  display: inline-block;
  animation: ${rockCoin} 5s ease-in-out infinite;
`;

const PillWrap = styled.div<{ $size: number }>`
  width: ${p => p.$size}px; height: ${p => p.$size}px;
  border-radius: ${p => Math.round(p.$size * 0.27)}px;
  background: linear-gradient(160deg, #38BDF8 0%, #2563EB 100%);
  display: flex; align-items: center; justify-content: center;
`;
const DarkWrap = styled.div<{ $size: number }>`
  width: ${p => p.$size}px; height: ${p => p.$size}px;
  border-radius: ${p => Math.round(p.$size * 0.22)}px;
  background: #1A2744;
  display: flex; align-items: center; justify-content: center;
`;


// ── Other DrSchmoe.ai logo options (kept for reference) ─────────────────────
export const DSPulse: React.FC<{ size?: number; white?: boolean }> = ({ size = 56, white = false }) => {
  const stroke = white ? 'white' : 'url(#ds-pulse-grad)';
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id="ds-pulse-grad" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#38BDF8" /><stop offset="55%" stopColor="#0EA5E9" /><stop offset="100%" stopColor="#2563EB" />
        </linearGradient>
      </defs>
      <circle cx="24" cy="24" r="21" stroke={stroke} strokeWidth="2.5" />
      <path d="M3,24 L11,24 L13,21 L15,24 L17,11 L19,37 L21,24 L24,19 L27,24 L45,24" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

export const DSQuad: React.FC<{ size?: number; white?: boolean }> = ({ size = 56, white = false }) => {
  const fill   = white ? 'rgba(255,255,255,0.95)' : 'url(#ds-quad-grad)';
  const stroke = white ? 'rgba(255,255,255,0.65)' : 'url(#ds-quad-grad)';
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id="ds-quad-grad" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#38BDF8" /><stop offset="100%" stopColor="#2563EB" />
        </linearGradient>
      </defs>
      <rect x="7" y="7" width="15" height="15" rx="4" fill={fill} />
      <rect x="26" y="7" width="15" height="15" rx="4" stroke={stroke} strokeWidth="2.5" />
      <rect x="7" y="26" width="15" height="15" rx="4" stroke={stroke} strokeWidth="2.5" />
      <rect x="26" y="26" width="15" height="15" rx="4" fill={fill} />
    </svg>
  );
};

export const DSNeural: React.FC<{ size?: number; white?: boolean }> = ({ size = 56, white = false }) => {
  const c = white ? 'white' : 'url(#ds-neural-grad)';
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <defs>
        <linearGradient id="ds-neural-grad" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#38BDF8" /><stop offset="100%" stopColor="#2563EB" />
        </linearGradient>
      </defs>
      <circle cx="24" cy="24" r="21" stroke={c} strokeWidth="1.5" strokeDasharray="3 3" />
      <line x1="24" y1="19" x2="24" y2="10" stroke={c} strokeWidth="2" strokeLinecap="round" />
      <line x1="29" y1="24" x2="38" y2="24" stroke={c} strokeWidth="2" strokeLinecap="round" />
      <line x1="24" y1="29" x2="24" y2="38" stroke={c} strokeWidth="2" strokeLinecap="round" />
      <line x1="19" y1="24" x2="10" y2="24" stroke={c} strokeWidth="2" strokeLinecap="round" />
      <circle cx="24" cy="8"  r="3.5" fill={c} />
      <circle cx="40" cy="24" r="3.5" fill={c} />
      <circle cx="24" cy="40" r="3.5" fill={c} />
      <circle cx="8"  cy="24" r="3.5" fill={c} />
      <circle cx="24" cy="24" r="6" fill={c} />
      <line x1="24" y1="21" x2="24" y2="27" stroke={white ? '#1A2744' : 'white'} strokeWidth="1.8" strokeLinecap="round" />
      <line x1="21" y1="24" x2="27" y2="24" stroke={white ? '#1A2744' : 'white'} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
};

// ── Preview Page ─────────────────────────────────────────────────────────────
const SZ = 64; // swatch container size

type LC = React.FC<{ size?: number; white?: boolean }>;

function SmallSwatch({ Logo, label, bg }: { Logo: LC; label: string; bg?: string }) {
  const inner = <Logo size={Math.round(SZ * 0.58)} white />;
  return (
    <div>
      {bg === 'pill'
        ? <PillWrap $size={SZ}>{inner}</PillWrap>
        : <DarkWrap $size={SZ}>{inner}</DarkWrap>}
      <SwatchLabel>{label}</SwatchLabel>
    </div>
  );
}

function LogoOption({ name, desc, Logo }: { name: string; desc: string; Logo: LC }) {
  return (
    <LogoCard>
      <SwatchRow>
        <div><Logo size={52} /><SwatchLabel>light</SwatchLabel></div>
        <SmallSwatch Logo={Logo} label="pill" bg="pill" />
        <SmallSwatch Logo={Logo} label="dark" />
      </SwatchRow>
      <LogoName>{name}</LogoName>
      <LogoDesc>{desc}</LogoDesc>
    </LogoCard>
  );
}

const LogoPreview: React.FC = () => (
  <Page>
    <H1>Logo Preview</H1>
    <Subtitle>Pick one per brand. Each shown on light, in gradient pill, and on dark background.</Subtitle>

    {/* ── Featured: Brain + AI ── */}
    <Section>
      <SectionTitle>DrSchmoe.ai — Featured Option</SectionTitle>
      <FeaturedCard>
        {/* Large light view */}
        <div>
          <DSBrainAI size={260} />
          <SwatchLabel style={{ marginTop: 8 }}>on light</SwatchLabel>
        </div>

        {/* Small swatches */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, justifyContent: 'center' }}>
          <SwatchRow>
            <SmallSwatch Logo={DSBrainAI} label="in pill" bg="pill" />
            <SmallSwatch Logo={DSBrainAI} label="on dark" />
          </SwatchRow>
          {/* Wordmark */}
          <WordmarkRow>
            <DSBrainAI size={36} />
            <div>
              <WordmarkText>DrSchmoe AI</WordmarkText>
              <WordmarkSub>NEURO CHIROPRACTIC</WordmarkSub>
            </div>
          </WordmarkRow>
        </div>

        {/* Info */}
        <FeaturedInfo>
          <FeaturedTitle>Brain + AI Nodes</FeaturedTitle>
          <FeaturedDesc>
            Brain silhouette from Recraft, palette-corrected to match the app's blue gradient.
            Neural network overlay (8 nodes, 13 edges) adds the AI/health intelligence angle —
            nodes represent brain regions communicating, center hub is the AI aggregation point.
          </FeaturedDesc>
          <div>
            <Tag>neuro chiro</Tag>
            <Tag>AI nodes</Tag>
            <Tag>brand gradient</Tag>
          </div>
        </FeaturedInfo>
      </FeaturedCard>
    </Section>

    {/* ── Other DrSchmoe options ── */}
    <Section>
      <SectionTitle>DrSchmoe.ai — Other Options</SectionTitle>
      <LogoGrid>
        <LogoOption name="A · Pulse Circle"  desc="Circle + ECG heartbeat line."  Logo={DSPulse} />
        <LogoOption name="B · Quad Mark"     desc="4 squares = the 4 profiles."   Logo={DSQuad} />
        <LogoOption name="C · Neural Node"   desc="Hub + 4 satellites."           Logo={DSNeural} />
      </LogoGrid>
    </Section>

    {/* ── Clinical Token — Featured ── */}
    <Section>
      <SectionTitle>Clinical Token — Featured</SectionTitle>
      <FeaturedCard>
        {/* Large on light */}
        <div>
          <SpinCoin><CTCoin size={240} /></SpinCoin>
          <SwatchLabel style={{ marginTop: 8 }}>on light</SwatchLabel>
        </div>

        {/* Swatches: gradient pill, dark */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, justifyContent: 'center' }}>
          <SwatchRow>
            <div>
              <div style={{
                width: 64, height: 64, borderRadius: 20,
                background: 'linear-gradient(160deg, #1565C0 0%, #0D47A1 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <SpinCoin><CTCoin size={50} white /></SpinCoin>
              </div>
              <SwatchLabel>in pill</SwatchLabel>
            </div>
            <div>
              <div style={{
                width: 64, height: 64, borderRadius: 14, background: '#1A2744',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <SpinCoin><CTCoin size={50} white /></SpinCoin>
              </div>
              <SwatchLabel>on dark</SwatchLabel>
            </div>
          </SwatchRow>
          {/* Wordmark */}
          <WordmarkRow>
            <SpinCoin><CTCoin size={36} /></SpinCoin>
            <div>
              <WordmarkText>Clinical Token</WordmarkText>
              <WordmarkSub>HEALTHTECH</WordmarkSub>
            </div>
          </WordmarkRow>
        </div>

        {/* Info */}
        <FeaturedInfo>
          <FeaturedTitle>Clinical Token</FeaturedTitle>
          <FeaturedDesc>
            A blue medical coin mark — stethoscope tubing forms a heart shape, uniting
            clinical care with token economics. The coin shape signals data as currency;
            the heart signals the patient at the center.
          </FeaturedDesc>
          <div>
            <Tag>clinical data</Tag>
            <Tag>token</Tag>
            <Tag>medical blue</Tag>
          </div>
        </FeaturedInfo>
      </FeaturedCard>
    </Section>

    {/* ── Clinical Token — Sizes ── */}
    <Section>
      <SectionTitle>Clinical Token — Sizes</SectionTitle>
      <LogoGrid>
        <LogoCard style={{ background: '#f5f8ff' }}>
          <SpinCoin><CTCoin size={160} /></SpinCoin>
          <LogoName>160 px</LogoName>
          <LogoDesc>Hero / splash screen</LogoDesc>
        </LogoCard>
        <LogoCard style={{ background: '#f5f8ff' }}>
          <SpinCoin><CTCoin size={96} /></SpinCoin>
          <LogoName>96 px</LogoName>
          <LogoDesc>Card header / feature tile</LogoDesc>
        </LogoCard>
        <LogoCard style={{ background: '#f5f8ff' }}>
          <SpinCoin><CTCoin size={56} /></SpinCoin>
          <LogoName>56 px</LogoName>
          <LogoDesc>Nav bar / list item</LogoDesc>
        </LogoCard>
        <LogoCard style={{ background: '#f5f8ff' }}>
          <SpinCoin><CTCoin size={32} /></SpinCoin>
          <LogoName>32 px</LogoName>
          <LogoDesc>Favicon / tab icon</LogoDesc>
        </LogoCard>
      </LogoGrid>
    </Section>

    {/* ── Clinical Token — Backgrounds ── */}
    <Section>
      <SectionTitle>Clinical Token — Backgrounds</SectionTitle>
      <LogoGrid>
        <LogoCard style={{ background: '#ffffff' }}>
          <SpinCoin><CTCoin size={100} /></SpinCoin>
          <LogoName>White</LogoName>
        </LogoCard>
        <LogoCard style={{ background: '#EEF4FF' }}>
          <SpinCoin><CTCoin size={100} /></SpinCoin>
          <LogoName>Light blue</LogoName>
        </LogoCard>
        <LogoCard style={{ background: '#1A2744' }}>
          <SpinCoin><CTCoin size={100} white /></SpinCoin>
          <LogoName style={{ color: '#fff' }}>Navy dark</LogoName>
        </LogoCard>
        <LogoCard style={{ background: 'linear-gradient(135deg, #1565C0, #0D47A1)' }}>
          <SpinCoin><CTCoin size={100} white /></SpinCoin>
          <LogoName style={{ color: '#fff' }}>Blue gradient</LogoName>
        </LogoCard>
      </LogoGrid>
    </Section>
  </Page>
);

export default LogoPreview;
